import * as React from 'react';
import { useRevalidator, useRouteLoaderData } from 'react-router-dom';
import { Await } from '@oysterjs/ui/async';
import { CopilotHomeV2 } from '~/components/home/copilot-home-v2';
import { CopilotHomeV2Loader } from '~/components/home/copilot-home-v2-loader';
import {
  Action,
  AgencyUserFieldsFragment,
  AgencyUserState,
  CustomFormListFieldsFragment,
  CustomPresetFieldsFragment,
  HtmlDocumentTemplateFieldsFragment,
  LoggedInAgencyUserFieldsFragment,
  Resource,
  SubscriptionPeriod,
  SubscriptionPlan,
  WordDocumentTemplateFieldsFragment,
  ExcelSpreadsheetTemplateFieldsFragment
} from '~/api/graphql';
import {
  ManageSubscriptionDialog,
  SelectPlanDialog,
  SubscriptionStatusBanner
} from '~/pages/settings/subscription';
import { dismissStandardSunsetRenewalBanner } from '~/components/settings/subscription/dismiss-renewal-banner';
import { usePermissions } from '~/components/permissions';
import { RenewalMigrationWizard } from '~/components/settings/subscription/renewal-migration-wizard';
import { AnnualRenewalWizard } from '~/components/settings/subscription/annual-renewal-wizard';
import { getStandardPlanRenewalDate } from '~/components/settings/subscription/renewal-billing-date';

// This is included just to capture the unlikely case that minSeats is not set in the subscription
// or the subscription is not found.
const FALLBACK_MIN_SEATS = 3;

export const HomePage = () => {
  const { hasPermission } = usePermissions();
  const revalidator = useRevalidator();
  const appLoader = useRouteLoaderData('app') as
    | {
        data: Promise<{
          agencyUsers: AgencyUserFieldsFragment[];
        }>;
      }
    | undefined;

  const homeLoader = useRouteLoaderData('home') as
    | {
        data: Promise<{
          agencyUser: LoggedInAgencyUserFieldsFragment;
          htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
          wordDocumentTemplates: WordDocumentTemplateFieldsFragment[];
          excelSpreadsheetTemplates: ExcelSpreadsheetTemplateFieldsFragment[];
          customPresets: CustomPresetFieldsFragment[];
          customForms: CustomFormListFieldsFragment[];
        }>;
      }
    | undefined;

  const mergedData = React.useMemo(() => {
    if (!appLoader?.data || !homeLoader?.data) {
      return null;
    }
    return Promise.all([appLoader.data, homeLoader.data]).then(([appData, homeData]) => ({
      ...appData,
      agencyUser: homeData.agencyUser,
      htmlDocumentTemplatesForPicker: homeData.htmlDocumentTemplates ?? [],
      wordDocumentTemplatesForPicker: homeData.wordDocumentTemplates ?? [],
      excelSpreadsheetTemplatesForPicker: homeData.excelSpreadsheetTemplates ?? [],
      customPresets: homeData.customPresets ?? [],
      customForms: homeData.customForms ?? []
    }));
  }, [appLoader, homeLoader]);

  const [showSelectPlanDialog, setShowSelectPlanDialog] = React.useState(false);
  const [showRenewalWizard, setShowRenewalWizard] = React.useState(false);
  const [showAnnualRenewalWizard, setShowAnnualRenewalWizard] = React.useState(false);
  const [showManageSubscriptionDialog, setShowManageSubscriptionDialog] = React.useState(false);
  const [manageSubscriptionPlan, setManageSubscriptionPlan] = React.useState<
    SubscriptionPlan | undefined
  >();
  const [manageSubscriptionPeriod, setManageSubscriptionPeriod] = React.useState<
    SubscriptionPeriod | undefined
  >();
  const [manageSubscriptionSeats, setManageSubscriptionSeats] = React.useState<
    number | undefined
  >();

  const onManageSubscription = async (
    plan: SubscriptionPlan,
    period: SubscriptionPeriod,
    seats?: number
  ) => {
    setManageSubscriptionPlan(plan);
    setManageSubscriptionPeriod(period);
    setManageSubscriptionSeats(seats);
    setShowSelectPlanDialog(false);
    setShowManageSubscriptionDialog(true);
  };

  return (
    <React.Suspense fallback={<CopilotHomeV2Loader />}>
      <Await resolve={mergedData ?? undefined}>
        {(data) =>
          data?.agencyUser && (
            <>
              {data.agencyUser.agency.subscription && (
                <div className="max-w-4xl mx-auto">
                  <SubscriptionStatusBanner
                    subscription={data.agencyUser.agency.subscription}
                    agencyName={data.agencyUser.agency.name}
                    onUpdateSubscription={() => setShowSelectPlanDialog(true)}
                    readOnly={!hasPermission(Resource.Subscription, Action.Update)}
                    onReviewRenewalOptions={() => setShowRenewalWizard(true)}
                    onDismissRenewalBanner={async () => {
                      await dismissStandardSunsetRenewalBanner();
                      revalidator.revalidate();
                    }}
                    onReviewAnnualRenewal={() => setShowAnnualRenewalWizard(true)}
                  />
                </div>
              )}
              <AnnualRenewalWizard
                open={showAnnualRenewalWizard}
                onClose={() => setShowAnnualRenewalWizard(false)}
                onCompleted={() => revalidator.revalidate()}
                agencyUser={data.agencyUser}
              />
              <SelectPlanDialog
                open={showSelectPlanDialog}
                onClose={() => setShowSelectPlanDialog(false)}
                onSelectPlan={(plan, period, seats) =>
                  onManageSubscription(
                    plan,
                    period,
                    seats ||
                      Math.max(
                        data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                        data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                          .length || 0
                      )
                  )
                }
                currentPlan={data.agencyUser.agency.subscription?.plan || SubscriptionPlan.Standard}
                currentPeriod={
                  data.agencyUser.agency.subscription?.period || SubscriptionPeriod.Monthly
                }
                currentSeats={
                  data.agencyUser.agency.subscription?.numSeats ||
                  Math.max(
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                    data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                      .length || 0
                  )
                }
                minSeats={Math.max(
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                  data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                    .length || 0
                )}
                includedSeats={
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS
                }
              />
              <ManageSubscriptionDialog
                open={showManageSubscriptionDialog}
                onClose={() => setShowManageSubscriptionDialog(false)}
                agencyUser={data.agencyUser}
                plan={
                  manageSubscriptionPlan ||
                  data.agencyUser?.agency.subscription?.plan ||
                  SubscriptionPlan.Pro
                }
                period={
                  manageSubscriptionPeriod ||
                  data.agencyUser?.agency.subscription?.period ||
                  SubscriptionPeriod.Monthly
                }
                seats={
                  manageSubscriptionSeats ||
                  data.agencyUser?.agency.subscription?.numSeats ||
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ||
                  FALLBACK_MIN_SEATS
                }
                voiceAgents={(data.agencyUser.agency.subscription?.numVoiceAgents || 1) - 1}
                minSeats={Math.max(
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                  data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                    .length || 0
                )}
                includedSeats={
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS
                }
              />
              <RenewalMigrationWizard
                open={showRenewalWizard}
                onClose={() => {
                  setShowRenewalWizard(false);
                }}
                onCompleted={() => {
                  revalidator.revalidate();
                }}
                agencyUser={data.agencyUser}
                agencyName={data.agencyUser.agency.name}
                renewalDate={getStandardPlanRenewalDate(data.agencyUser.agency.subscription)}
                currentPeriod={
                  data.agencyUser.agency.subscription?.period || SubscriptionPeriod.Monthly
                }
                currentSeats={
                  data.agencyUser.agency.subscription?.numSeats ||
                  Math.max(
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                    data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                      .length || 0
                  )
                }
                minSeats={Math.max(
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                  data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                    .length || 0
                )}
                includedSeats={
                  data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS
                }
                voiceAgents={Math.max(
                  0,
                  (data.agencyUser.agency.subscription?.numVoiceAgents ?? 1) - 1
                )}
              />
              <CopilotHomeV2
                agencyUser={data.agencyUser}
                customForms={data.customForms ?? []}
                htmlDocumentTemplatesForPicker={data.htmlDocumentTemplatesForPicker ?? []}
                wordDocumentTemplatesForPicker={data.wordDocumentTemplatesForPicker ?? []}
                excelSpreadsheetTemplatesForPicker={data.excelSpreadsheetTemplatesForPicker ?? []}
                customPresets={data.customPresets ?? []}
              />
            </>
          )
        }
      </Await>
    </React.Suspense>
  );
};
